import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  EMPTY_TENANT_AUDIT_FILTERS,
  useRefreshTenantAudit,
  useTenantAudit,
  type TenantAuditFilters,
} from '../../api/audit.js';
import type { Membership, Tenant, TenantAuditEntry } from '../../api/types.js';
import { EmptyState } from '../../components/EmptyState.js';
import { AuditRowDrawer } from './AuditRowDrawer.js';
import { PageIdentity } from '../../shell/PageIdentity.js';
import { PageWrapper } from '../../shell/PageWrapper.js';

const ACTION_PREFIXES: Array<{ value: string; label: string }> = [
  { value: '', label: 'Toutes les actions' },
  { value: 'invoice.', label: 'invoice.*' },
  { value: 'invoice.dispute', label: 'invoice.dispute' },
  { value: 'validation.', label: 'validation.*' },
  { value: 'admin.member.', label: 'admin.member.*' },
  { value: 'dashboard.', label: 'dashboard.*' },
  { value: 'tenant.', label: 'tenant.*' },
];

interface Props {
  tenants: Tenant[];
  memberships: Membership[];
}

const ADMIN_ROLES: Membership['role'][] = ['group_admin', 'ap_admin'];

function defaultSince(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString();
}

function actionChipClass(action: string): string {
  if (action.startsWith('invoice.dispute')) return 'pill pill--danger';
  if (action.startsWith('invoice.')) return 'pill pill--info';
  if (action.startsWith('admin.')) return 'pill pill--neutral';
  if (action.startsWith('validation.')) return 'pill pill--warning';
  if (action.startsWith('dashboard.')) return 'pill pill--neutral';
  return 'pill pill--neutral';
}

function formatDateTimeFR(iso: string): string {
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  } catch {
    return iso;
  }
}

export function AdminAuditScreen({ tenants, memberships }: Props) {
  const [sp, setSp] = useSearchParams();
  const adminTenantIds = useMemo(
    () => memberships.filter((m) => ADMIN_ROLES.includes(m.role)).map((m) => m.tenantId),
    [memberships],
  );
  const tenantsInScope = useMemo(
    () => tenants.filter((t) => adminTenantIds.includes(t.tenantId)),
    [tenants, adminTenantIds],
  );

  const initialTenantId = sp.get('tenantId');
  const fallbackTenantId = tenantsInScope[0]?.tenantId ?? '';
  const [tenantId, setTenantId] = useState<string>(
    initialTenantId && tenantsInScope.some((t) => t.tenantId === initialTenantId)
      ? initialTenantId
      : fallbackTenantId,
  );

  const [filters, setFilters] = useState<TenantAuditFilters>({
    action: sp.get('action') ?? '',
    actorUid: sp.get('actorUid') ?? '',
    since: sp.get('since') ?? defaultSince(),
  });

  const [pages, setPages] = useState<TenantAuditEntry[][]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [drawerEntry, setDrawerEntry] = useState<TenantAuditEntry | null>(null);

  const query = useTenantAudit({ tenantId, filters, cursor });
  const refresh = useRefreshTenantAudit();

  // Reset pagination when tenant or filters change.
  const filterKey = `${tenantId}|${filters.action}|${filters.actorUid}|${filters.since ?? ''}`;
  useEffect(() => {
    setPages([]);
    setCursor(null);
  }, [filterKey]);

  // Merge incoming page into the page list — replace at cursor=null,
  // append otherwise. Keyed on `incoming` reference so we don't double-add.
  const incoming = query.data;
  useEffect(() => {
    if (!incoming) return;
    if (cursor === null) {
      setPages([incoming.data]);
    } else {
      setPages((p) => {
        const tip = p[p.length - 1];
        if (tip && tip[0]?.auditId === incoming.data[0]?.auditId) return p;
        return [...p, incoming.data];
      });
    }
  }, [incoming, cursor]);

  function applyUrl(next: { tenantId?: string; filters?: TenantAuditFilters }) {
    const tid = next.tenantId ?? tenantId;
    const f = next.filters ?? filters;
    const params = new URLSearchParams();
    if (tid) params.set('tenantId', tid);
    if (f.action) params.set('action', f.action);
    if (f.actorUid) params.set('actorUid', f.actorUid);
    if (f.since) params.set('since', f.since);
    setSp(params, { replace: true });
  }

  function onTenantChange(next: string) {
    setTenantId(next);
    applyUrl({ tenantId: next });
  }
  function onFilterChange(next: Partial<TenantAuditFilters>) {
    const merged = { ...filters, ...next };
    setFilters(merged);
    applyUrl({ filters: merged });
  }
  function clearAll() {
    const next = { ...EMPTY_TENANT_AUDIT_FILTERS, since: defaultSince() };
    setFilters(next);
    applyUrl({ filters: next });
  }
  function loadMore() {
    if (query.data?.nextCursor) {
      setCursor(query.data.nextCursor);
    }
  }

  const rows = pages.flat();
  const hasActiveFilter =
    filters.action.length > 0 ||
    filters.actorUid.length > 0 ||
    (filters.since != null && filters.since !== defaultSince());

  return (
    <PageWrapper>
      <section className="annuaire" aria-label="Journal d'audit">
        <PageIdentity
          testId="audit"
          eyebrow="ADMINISTRATION · GLOBAL"
          title="Journal d'audit"
          subtitle="Historique horodaté des actions opérateur sur cette société. Filtrage par action, acteur et plage temporelle."
          actions={
            <button
              type="button"
              className="btn-ghost"
              onClick={() => refresh(tenantId)}
              disabled={query.isFetching}
              aria-label="Actualiser"
              data-testid="audit-refresh"
            >
              {query.isFetching ? '…' : '⟳'}
            </button>
          }
        />

        <div className="invoices__advanced-filters" data-testid="audit-filters">
          <label className="invoices__advanced-field">
            <span className="eyebrow">Société</span>
            <select
              value={tenantId}
              onChange={(e) => onTenantChange(e.target.value)}
              data-testid="audit-tenant-filter"
            >
              {tenantsInScope.map((t) => (
                <option key={t.tenantId} value={t.tenantId}>
                  {t.legalName}
                </option>
              ))}
            </select>
          </label>
          <label className="invoices__advanced-field">
            <span className="eyebrow">Action</span>
            <select
              value={filters.action}
              onChange={(e) => onFilterChange({ action: e.target.value })}
              data-testid="audit-action-filter"
            >
              {ACTION_PREFIXES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label className="invoices__advanced-field">
            <span className="eyebrow">Acteur UID</span>
            <input
              type="text"
              placeholder="u-..."
              value={filters.actorUid}
              onChange={(e) => onFilterChange({ actorUid: e.target.value })}
              data-testid="audit-actor-filter"
            />
          </label>
          <label className="invoices__advanced-field">
            <span className="eyebrow">Depuis</span>
            <input
              type="date"
              value={filters.since ? filters.since.slice(0, 10) : ''}
              onChange={(e) =>
                onFilterChange({
                  since: e.target.value
                    ? new Date(`${e.target.value}T00:00:00.000Z`).toISOString()
                    : null,
                })
              }
              data-testid="audit-since-filter"
            />
          </label>
          {hasActiveFilter ? (
            <button
              type="button"
              className="btn-ghost invoices__clear-filters"
              onClick={clearAll}
              data-testid="audit-filter-clear"
            >
              Effacer les filtres
            </button>
          ) : null}
        </div>

        {query.isError ? (
          <div className="invoices__error" role="alert" data-testid="audit-error">
            <span>Impossible de charger le journal d'audit.</span>
            <button type="button" className="btn-ghost" onClick={() => query.refetch()}>
              Réessayer
            </button>
          </div>
        ) : null}

        {query.isLoading && rows.length === 0 ? (
          <p className="muted" data-testid="audit-loading">
            Chargement…
          </p>
        ) : rows.length === 0 && !query.isError ? (
          <EmptyState
            testId="audit-empty"
            variant="card"
            icon="📜"
            title="Aucune action enregistrée"
            description="Aucune entrée d'audit ne correspond à vos filtres."
          />
        ) : (
          <table className="audit-table" data-testid="audit-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Action</th>
                <th>Acteur</th>
                <th>Cible</th>
                <th>Résultat</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <AuditRow
                  key={e.auditId}
                  entry={e}
                  tenantId={tenantId}
                  onOpen={() => setDrawerEntry(e)}
                />
              ))}
            </tbody>
          </table>
        )}

        {query.data?.nextCursor && rows.length > 0 ? (
          <div className="invoices__pager">
            <button
              type="button"
              className="btn-ghost"
              onClick={loadMore}
              disabled={query.isFetching}
              data-testid="audit-load-more"
            >
              {query.isFetching ? 'Chargement…' : 'Charger plus'}
            </button>
          </div>
        ) : null}

        {drawerEntry ? (
          <AuditRowDrawer entry={drawerEntry} onClose={() => setDrawerEntry(null)} />
        ) : null}
      </section>
    </PageWrapper>
  );
}

interface RowProps {
  entry: TenantAuditEntry;
  tenantId: string;
  onOpen: () => void;
}

function AuditRow({ entry, tenantId, onOpen }: RowProps) {
  const actor = entry.actorDisplayName ?? entry.actorEmail ?? entry.actorUid ?? 'Système';
  return (
    <tr data-testid={`audit-row-${entry.auditId}`}>
      <td className="muted" style={{ whiteSpace: 'nowrap' }}>
        {formatDateTimeFR(entry.ts)}
      </td>
      <td>
        <span className={actionChipClass(entry.action)}>{entry.action}</span>
      </td>
      <td>{actor}</td>
      <td>
        {entry.primaryKey ? (
          <Link
            to={`/t/${tenantId}/invoices/${encodeURIComponent(entry.primaryKey)}`}
            style={{ fontFamily: 'monospace', fontSize: 12 }}
          >
            {entry.primaryKey.slice(0, 32)}
            {entry.primaryKey.length > 32 ? '…' : ''}
          </Link>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td>
        <span className={entry.outcome === 'ok' ? 'pill pill--success' : 'pill pill--danger'}>
          {entry.outcome === 'ok' ? 'OK' : 'Échec'}
        </span>
      </td>
      <td>
        <button
          type="button"
          className="btn-ghost"
          onClick={onOpen}
          aria-label="Voir les détails"
          data-testid={`audit-row-${entry.auditId}-open`}
        >
          ▸
        </button>
      </td>
    </tr>
  );
}
